import { UpdateProjectContent } from 'api/graphQL/reactiveVars/workspaceContent'
import {
    ContentInsertVariables,
    InsertContentItemsFromHtmlResponse,
} from 'api/graphQL/types/contentInsert.types'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import { useNotification } from 'components/WorkspaceEditor/services'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback } from 'react'
import { usePasteContentItemsFromHtmlMutation } from '../api/mutations/copyPasteContentMutations'
import { useCopyPasteShallowSelector } from '../store/CopyPasteStore'

const PASTE_UNSPECIFIED_ERROR_MESSAGE =
    'An unspecified error was encountered while pasting your content.'

interface UseHtmlPasteMutationHandlerProps {
    contentInsertVariables: Partial<ContentInsertVariables>
    setInstanceId: (instanceId: string) => void
    setWarningText: (warningText: string) => void
    setIsPasteInProgress: (isPasteInProgress: boolean) => void
    isWarningIgnored: boolean
}

export const useHtmlPasteMutationHandler = ({
    contentInsertVariables,
    setInstanceId,
    setWarningText,
    setIsPasteInProgress,
    isWarningIgnored,
}: UseHtmlPasteMutationHandlerProps) => {
    const notification = useNotification()

    const {
        activeTab,
        setIsTreeLoading,
        contentItemViewNodes,
    } = useShallowSelector((state) => ({
        activeTab: state.activeTab,
        setIsTreeLoading: state.actions.setIsTreeLoading,
        contentItemViewNodes: state.contentItemViewNodes,
    }))

    const { resetCopyPasteState } = useCopyPasteShallowSelector((state) => ({
        resetCopyPasteState: state.resetCopyPasteState,
    }))

    const { getContentItemViewNodesAPI } = useProductFetcher({
        overrideContentItemNodes: true,
        onError: () => {
            setIsTreeLoading(false)
        },
    })

    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: false,
    })

    const handlePasteCompleted = useCallback(
        async (result: Partial<UpdateProjectContent>) => {
            setIsPasteInProgress(false)
            notification.success({ message: 'Changes Saved Successfully' })
            setIsTreeLoading(true)

            const targetRootArticleIds = contentInsertVariables
                .rootArticleContentItemIds?.length
                ? contentInsertVariables.rootArticleContentItemIds
                : null

            if (result.contentItemNodes?.length) {
                const response = {
                    getProjectProductFamilyView: {
                        contentItemViewNodes: result.contentItemNodes,
                    },
                }
                await onContentViewFetchSuccess(
                    response,
                    [],
                    false,
                    contentItemViewNodes
                )
            } else {
                await getContentItemViewNodesAPI(
                    targetRootArticleIds,
                    activeTab
                )
            }

            resetCopyPasteState()
            setIsTreeLoading(false)
        },
        [
            notification,
            resetCopyPasteState,
            getContentItemViewNodesAPI,
            onContentViewFetchSuccess,
            activeTab,
            setIsTreeLoading,
            contentInsertVariables,
            setIsPasteInProgress,
            contentItemViewNodes,
        ]
    )

    const handlePasteError = useCallback(
        (errorMessage: string | null | undefined) => {
            setIsTreeLoading(false)
            notification.error({
                message: errorMessage ?? PASTE_UNSPECIFIED_ERROR_MESSAGE,
            })
            resetCopyPasteState()
        },
        [notification, resetCopyPasteState, setIsTreeLoading]
    )

    const { pasteContentItemsFromHtml } = usePasteContentItemsFromHtmlMutation(
        (data: InsertContentItemsFromHtmlResponse) => {
            if (data.insertContentItemsFromHtml?.warnings?.length) {
                setWarningText(data.insertContentItemsFromHtml.warnings[0])
            } else if (data?.insertContentItemsFromHtml?.instanceId) {
                setInstanceId(data?.insertContentItemsFromHtml?.instanceId)
            } else {
                handlePasteError(
                    'Server returned an invalid response; cancelling paste.'
                )
            }
        },
        (error) => {
            handlePasteError(error.message)
        }
    )

    const executePasteMutation = useCallback(() => {
        if (contentInsertVariables) {
            setIsPasteInProgress(true)
            pasteContentItemsFromHtml({
                variables: {
                    input: {
                        ...contentInsertVariables.input!,
                        ignoreValidationWarnings: isWarningIgnored,
                    },
                    partType: contentInsertVariables.partType!,
                    productFamilyId: contentInsertVariables.productFamilyId!,
                    rootArticleContentItemIds: contentInsertVariables.rootArticleContentItemIds!,
                },
            })
        }
    }, [
        contentInsertVariables,
        isWarningIgnored,
        pasteContentItemsFromHtml,
        setIsPasteInProgress,
    ])

    return {
        handlePasteCompleted,
        handlePasteError,
        executePasteMutation,
    }
}
